import type { QuarterMode, Request, TabState } from '../types';
import { hasDateRange, matchesDateRange } from './dateRange';
import { getQuarterMode, isAllFY, isAllQ, matchesSub, requestQuarter, requestRangeDate } from './filter';

// ─── TrendChart buckets (ADR 0010) ────────────────────────────────────────
// One bucket per selected FY × selected quarter, on the tab's quarter-mode fields.
// 'All' FY expands to every FY present in the matching requests; 'All' Q to Q1–Q4.
// When either side was expanded from 'All', empty buckets at both ends are trimmed
// so the chart doesn't show quarters before the data starts or after it ends.

export const TREND_QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

export interface TrendPoint {
  quarter: string;  // e.g. 'FY26 Q3'
  requests: number;
  gnarr: number;    // $M, 2dp
}

export interface TrendFilter {
  region: string;
  sub: string[];
  status: string;
  state: Pick<TabState, 'fy' | 'q' | 'qMode' | 'dateFrom' | 'dateTo'>;
}

export function trendLabel(fy: string, q: string): string {
  return `FY${fy.slice(-2)} ${q}`;
}

export function buildTrendData(requests: Request[], f: TrendFilter): TrendPoint[] {
  const mode: QuarterMode = getQuarterMode(f.state);
  const matching = requests.filter(r =>
    (f.region === 'Global' || r.opp_region === f.region) &&
    matchesSub(f.sub, r.req_sub_region) &&
    (!f.status || f.status === 'All' || r.req_status === f.status) &&
    (!hasDateRange(f.state) || matchesDateRange(requestRangeDate(r, mode), f.state)),
  );

  const allFy = isAllFY(f.state.fy);
  const allQ = isAllQ(f.state.q);
  const fyList = allFy
    ? [...new Set(matching.map(r => requestQuarter(r, mode).fy).filter(Boolean))].sort()
    : [...f.state.fy].filter(y => y !== 'All').sort();
  const qList = allQ
    ? [...TREND_QUARTERS]
    : TREND_QUARTERS.filter(q => f.state.q.includes(q));

  const counts = new Map<string, { n: number; g: number }>();
  const keys: string[] = [];
  for (const fy of fyList) {
    for (const q of qList) {
      const key = `${fy}|${q}`;
      keys.push(key);
      counts.set(key, { n: 0, g: 0 });
    }
  }

  for (const r of matching) {
    const { fy, q } = requestQuarter(r, mode);
    const bucket = counts.get(`${fy}|${q}`);
    if (!bucket) continue;
    bucket.n++;
    bucket.g += r.opp_gnarr;
  }

  let start = 0;
  let end = keys.length;
  if (allFy || allQ) {
    while (start < end && counts.get(keys[start])!.n === 0) start++;
    while (end > start && counts.get(keys[end - 1])!.n === 0) end--;
  }

  return keys.slice(start, end).map(key => {
    const [fy, q] = key.split('|');
    const { n, g } = counts.get(key)!;
    return { quarter: trendLabel(fy, q), requests: n, gnarr: Math.round(g / 1e4) / 100 };
  });
}
